import React, { useEffect, useState } from "react";
import { Layout, Menu, Avatar, Dropdown, Drawer, Button } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserOutlined, LogoutOutlined, MenuOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { usePermissions } from "../../../hooks/usePermissions";
import { LanguageSwitcher, ReminderCenter } from "../../common";
import {
  AUTH_REDIRECT_REASON_STORAGE_KEY,
  POST_LOGIN_REDIRECT_STORAGE_KEY,
} from "../../../utils/axiosConfig";
import { loadTiandituApi } from "../../../utils/tianditu";
import "./ResponsiveHeader.css";

const { Header } = Layout;
const DATA_CENTER_MENU_KEY = "data-center";
const PAYMENT_MENU_KEY = "payment-center";
const OPERATIONS_CENTER_MENU_KEY = "operations-center";
const SYSTEM_MANAGEMENT_MENU_KEY = "system-management";
const PROJECT_MANAGEMENT_MENU_KEY = "project-management";
const MODEL_MANAGEMENT_MENU_KEY = "model-management";
const DATA_CENTER_ROUTE_KEYS = [
  "/statistics",
  "/statistics/species-analysis",
  "/statistics/trend-analysis",
  "/device-mosquito",
  "/history",
];
const PAYMENT_ROUTE_KEYS = ["/orders", "/admin/payment-merchants"];
const OPERATIONS_CENTER_ROUTE_KEYS = [
  "/admin/operations/device-upgrade-bindings",
  "/admin/operations/device-upgrade-artifacts",
  "/admin/operations/device-upgrade-batches",
];
const SYSTEM_MANAGEMENT_ROUTE_KEYS = [
  "/admin/users",
  "/admin/roles",
  "/admin/regions",
  "/admin/system-settings",
  "/admin/operation-logs",
];
const PROJECT_MANAGEMENT_ROUTE_KEYS = [
  "/admin/package-management",
  "/admin/project-management-v1",
  "/admin/project-management-v1/viewers",
];
const MODEL_MANAGEMENT_ROUTE_KEYS = [
  "/admin/model-management/upload",
  "/admin/model-management/configuration",
  "/admin/model-management/types",
  "/admin/model-management/evaluations",
];

const normalizePathname = (pathname: string) => {
  if (pathname.startsWith("/admin/project-management-v1/viewers")) {
    return "/admin/project-management-v1/viewers";
  }

  if (pathname.startsWith("/admin/project-management-v1")) {
    return "/admin/project-management-v1";
  }

  if (pathname.startsWith("/admin/package-management")) {
    return "/admin/package-management";
  }

  if (pathname.startsWith("/admin/operations/device-upgrade-bindings")) {
    return "/admin/operations/device-upgrade-bindings";
  }

  if (pathname.startsWith("/admin/operations/device-upgrade-artifacts")) {
    return "/admin/operations/device-upgrade-artifacts";
  }

  if (pathname.startsWith("/admin/operations/device-upgrade-batches")) {
    return "/admin/operations/device-upgrade-batches";
  }

  if (pathname.startsWith("/statistics/species-analysis")) {
    return "/statistics/species-analysis";
  }

  if (pathname.startsWith("/statistics/trend-analysis")) {
    return "/statistics/trend-analysis";
  }

  if (pathname.startsWith("/admin/model-management/types")) {
    return "/admin/model-management/types";
  }

  if (
    pathname.startsWith("/admin/model-management/validation-datasets") ||
    pathname.startsWith("/admin/model-management/validation-rules") ||
    pathname.startsWith("/admin/model-management/evaluations")
  ) {
    return "/admin/model-management/evaluations";
  }

  if (pathname.startsWith("/admin/model-management/configuration")) {
    return "/admin/model-management/configuration";
  }

  if (pathname.startsWith("/admin/model-management")) {
    return "/admin/model-management/upload";
  }

  return pathname;
};

const getOpenMenuKeys = (pathname: string) => {
  const normalizedPathname = normalizePathname(pathname);
  const openKeys: string[] = [];

  if (DATA_CENTER_ROUTE_KEYS.includes(normalizedPathname)) {
    openKeys.push(DATA_CENTER_MENU_KEY);
  }

  if (PAYMENT_ROUTE_KEYS.includes(normalizedPathname)) {
    openKeys.push(PAYMENT_MENU_KEY);
  }

  if (OPERATIONS_CENTER_ROUTE_KEYS.includes(normalizedPathname)) {
    openKeys.push(OPERATIONS_CENTER_MENU_KEY);
  }

  if (SYSTEM_MANAGEMENT_ROUTE_KEYS.includes(normalizedPathname)) {
    openKeys.push(SYSTEM_MANAGEMENT_MENU_KEY);
  }

  if (PROJECT_MANAGEMENT_ROUTE_KEYS.includes(normalizedPathname)) {
    openKeys.push(PROJECT_MANAGEMENT_MENU_KEY);
  }

  if (MODEL_MANAGEMENT_ROUTE_KEYS.includes(normalizedPathname)) {
    openKeys.push(MODEL_MANAGEMENT_MENU_KEY);
  }

  return openKeys;
};

const ResponsiveHeader: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, authStatus, logout } = useAuth();
  const { t } = useTranslation("navigation");
  const {
    canAccessDeviceDistribution,
    canAccessDeviceManagement,
    canReadDeviceUpgradeBindings,
    canReadDeviceUpgradeArtifacts,
    canReadDeviceUpgradeBatches,
    canAccessAnnotationManagement,
    canAccessManualRecognitionPage,
    canAccessHistory,
    canAccessHome,
    canAccessOrderManagement,
    canAccessOperationLogs,
    canAccessPaymentMerchantManagement,
    canAccessReviewExport,
    canAccessRegionManagement,
    canAccessRoleManagement,
    canAccessSpeciesAnalysis,
    canAccessUserManagement,
    canAccessSystemSettings,
    canAccessTrendAnalysis,
    canAccessPackageManagement,
    canAccessModelManagement,
    canAccessModelValidation,
    canAccessProjectManagement,
    canAccessProjectViewerBoard,
    isPlatformAccount,
  } = usePermissions();

  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [desktopOpenKeys, setDesktopOpenKeys] = useState<string[]>([]);
  const [mobileOpenKeys, setMobileOpenKeys] = useState<string[]>([]);
  const isAuthenticated = authStatus === "authenticated" && !!user;
  const selectedKey = normalizePathname(location.pathname);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setDesktopOpenKeys([]);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile || desktopOpenKeys.length === 0) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Element | null;

      if (
        target?.closest(".responsive-header") ||
        target?.closest(".ant-menu-submenu-popup")
      ) {
        return;
      }

      setDesktopOpenKeys([]);
    };

    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [desktopOpenKeys.length, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      setMobileOpenKeys([]);
      return;
    }

    setMobileOpenKeys(getOpenMenuKeys(location.pathname));
  }, [isMobile, location.pathname]);

  const handleDeviceMosquitoLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    setDrawerVisible(false);
    if (location.pathname === "/device-mosquito") {
      event.preventDefault();
      navigate("/device-mosquito", {
        replace: true,
        state: { refreshMapAt: Date.now() },
      });
    }
  };

  const handleHistoryLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    setDrawerVisible(false);
    if (location.pathname === "/history") {
      event.preventDefault();
      navigate("/history", {
        replace: true,
        state: { refreshHistoryAt: Date.now() },
      });
    }
  };

  const preloadDeviceDistributionMap = () => {
    void loadTiandituApi().catch(() => undefined);
  };

  const handleLogout = () => {
    logout();
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_REDIRECT_REASON_STORAGE_KEY);
    setDrawerVisible(false);
    navigate("/login", { replace: true });
  };

  const paymentMenuItems = [
    ...(canAccessOrderManagement
      ? [
          {
            key: "/orders",
            label: (
              <Link to="/orders" onClick={() => setDrawerVisible(false)}>
                {t("menu.orderManagement")}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessPaymentMerchantManagement
      ? [
          {
            key: "/admin/payment-merchants",
            label: (
              <Link to="/admin/payment-merchants" onClick={() => setDrawerVisible(false)}>
                {t("menu.paymentMerchantManagement")}
              </Link>
            ),
          },
        ]
      : []),
  ];

  const dataCenterMenuItems = [
    ...(canAccessSpeciesAnalysis
      ? [
          {
            key: "/statistics/species-analysis",
            label: (
              <Link
                to="/statistics/species-analysis"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.speciesAnalysis", {
                  defaultValue: "蚊虫种类分析",
                })}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessTrendAnalysis
      ? [
          {
            key: "/statistics/trend-analysis",
            label: (
              <Link
                to="/statistics/trend-analysis"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.trendAnalysis", {
                  defaultValue: "蚊虫趋势分析",
                })}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessDeviceDistribution
      ? [
          {
            key: "/device-mosquito",
            label: (
              <Link
                to="/device-mosquito"
                onClick={handleDeviceMosquitoLinkClick}
                onFocus={preloadDeviceDistributionMap}
                onMouseEnter={preloadDeviceDistributionMap}
                style={{ display: "block", width: "100%" }}
              >
                {t("menu.deviceMosquito")}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessHistory
      ? [
          {
            key: "/history",
            label: (
              <Link to="/history" onClick={handleHistoryLinkClick}>
                {t("menu.history")}
              </Link>
            ),
          },
        ]
      : []),
  ];

  const projectManagementMenuItems = [
    ...(canAccessProjectManagement
      ? [
          {
            key: "/admin/project-management-v1",
            label: (
              <Link
                to="/admin/project-management-v1"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.projectManagementV1", {
                  defaultValue: t("menu.projectManagement"),
                })}
              </Link>
            ),
          },
        ]
      : []),
    ...(isPlatformAccount && canAccessProjectViewerBoard
      ? [
          {
            key: "/admin/project-management-v1/viewers",
            label: (
              <Link
                to="/admin/project-management-v1/viewers"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.viewerAccountBoard", {
                  defaultValue: t("menu.projectViewerScopes"),
                })}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessPackageManagement
      ? [
          {
            key: "/admin/package-management",
            label: (
              <Link
                to="/admin/package-management"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.packageManagementV1", {
                  defaultValue: "设备版本",
                })}
              </Link>
            ),
          },
        ]
      : []),
  ];

  const operationsCenterMenuItems = [
    ...(canReadDeviceUpgradeBindings
      ? [
          {
            key: "/admin/operations/device-upgrade-bindings",
            label: (
              <Link
                to="/admin/operations/device-upgrade-bindings"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.deviceUpgradeBindings", {
                  defaultValue: "设备绑定",
                })}
              </Link>
            ),
          },
        ]
      : []),
    ...(canReadDeviceUpgradeArtifacts
      ? [
          {
            key: "/admin/operations/device-upgrade-artifacts",
            label: (
              <Link
                to="/admin/operations/device-upgrade-artifacts"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.deviceUpgradeArtifacts", {
                  defaultValue: "升级包管理",
                })}
              </Link>
            ),
          },
        ]
      : []),
    ...(canReadDeviceUpgradeBatches
      ? [
          {
            key: "/admin/operations/device-upgrade-batches",
            label: (
              <Link
                to="/admin/operations/device-upgrade-batches"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.deviceUpgradeBatches", {
                  defaultValue: "升级批次管理",
                })}
              </Link>
            ),
          },
        ]
      : []),
  ];

  const modelManagementMenuItems = [
    ...(canAccessModelManagement ? [{
      key: "/admin/model-management/upload",
      label: (
        <Link
          to="/admin/model-management/upload"
          onClick={() => setDrawerVisible(false)}
        >
          {t("menu.modelUpload", { defaultValue: "模型上传" })}
        </Link>
      ),
    }, {
      key: "/admin/model-management/configuration",
      label: (
        <Link
          to="/admin/model-management/configuration"
          onClick={() => setDrawerVisible(false)}
        >
          {t("menu.modelConfiguration", { defaultValue: "模型配置" })}
        </Link>
      ),
    }, {
      key: "/admin/model-management/types",
      label: (
        <Link
          to="/admin/model-management/types"
          onClick={() => setDrawerVisible(false)}
        >
          {t("menu.modelTypeConfiguration", { defaultValue: "类型配置" })}
        </Link>
      ),
    }] : []),
    ...(canAccessModelValidation ? [{
      key: "/admin/model-management/evaluations",
      label: (
        <Link
          to="/admin/model-management/evaluations"
          onClick={() => setDrawerVisible(false)}
        >
          {t("menu.modelValidation", { defaultValue: "效果验证" })}
        </Link>
      ),
    }] : []),
  ];

  const systemManagementMenuItems = [
    ...(canAccessUserManagement
      ? [
          {
            key: "/admin/users",
            label: (
              <Link to="/admin/users" onClick={() => setDrawerVisible(false)}>
                {t("menu.userManagement")}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessRoleManagement
      ? [
          {
            key: "/admin/roles",
            label: (
              <Link to="/admin/roles" onClick={() => setDrawerVisible(false)}>
                {t("menu.roleManagement")}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessRegionManagement
      ? [
          {
            key: "/admin/regions",
            label: (
              <Link to="/admin/regions" onClick={() => setDrawerVisible(false)}>
                {t("menu.regionManagement")}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessSystemSettings
      ? [
          {
            key: "/admin/system-settings",
            label: (
              <Link
                to="/admin/system-settings"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.systemSettings", {
                  defaultValue: "系统设置",
                })}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessOperationLogs
      ? [
          {
            key: "/admin/operation-logs",
            label: (
              <Link
                to="/admin/operation-logs"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.operationLog", {
                  defaultValue: "操作日志",
                })}
              </Link>
            ),
          },
        ]
      : []),
  ];

  const adminMenuItems = [
    ...(systemManagementMenuItems.length > 0
      ? [
          {
            key: SYSTEM_MANAGEMENT_MENU_KEY,
            label: t("menu.systemManagement"),
            children: systemManagementMenuItems,
          },
        ]
      : []),
    ...(canAccessModelManagement || canAccessModelValidation
      ? [
          {
            key: MODEL_MANAGEMENT_MENU_KEY,
            label: t("menu.modelManagement", {
              defaultValue: "模型管理",
            }),
            children: modelManagementMenuItems,
          },
        ]
      : []),
    ...(projectManagementMenuItems.length > 0
      ? [
          {
            key: PROJECT_MANAGEMENT_MENU_KEY,
            label: t("menu.projectManagementGroup", {
              defaultValue: t("menu.projectManagement"),
            }),
            children: projectManagementMenuItems,
          },
        ]
      : []),
    ...(operationsCenterMenuItems.length > 0
      ? [
          {
            key: OPERATIONS_CENTER_MENU_KEY,
            label: t("menu.operationsCenter", {
              defaultValue: "运维中心",
            }),
            children: operationsCenterMenuItems,
          },
        ]
      : []),
    ...(paymentMenuItems.length > 0
      ? [
          {
            key: PAYMENT_MENU_KEY,
            label: t("menu.paymentCenter"),
            children: paymentMenuItems,
          },
        ]
      : []),
  ];

  const menuItems = [
    ...(!isAuthenticated || canAccessHome
      ? [
          {
            key: "/",
            label: (
              <Link to="/" onClick={() => setDrawerVisible(false)}>
                {t("menu.home")}
              </Link>
            ),
          },
        ]
      : []),
    ...(dataCenterMenuItems.length > 0
      ? [
          {
            key: DATA_CENTER_MENU_KEY,
            label: t("menu.dataCenter"),
            children: dataCenterMenuItems,
          },
        ]
      : []),
    ...(canAccessManualRecognitionPage
      ? [
          {
            key: "/manual-recognition",
            label: (
              <Link
                to="/manual-recognition"
                onClick={() => setDrawerVisible(false)}
              >
                {t("menu.manualRecognition")}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessDeviceManagement
      ? [
          {
            key: "/device-management",
            label: (
              <Link to="/device-management" onClick={() => setDrawerVisible(false)}>
                {t("menu.deviceManagement")}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessAnnotationManagement
      ? [
          {
            key: "/annotation-management",
            label: (
              <Link to="/annotation-management" onClick={() => setDrawerVisible(false)}>
                {t("menu.annotationManagement")}
              </Link>
            ),
          },
        ]
      : []),
    ...(canAccessReviewExport
      ? [
          {
            key: "/review-export",
            label: (
              <Link to="/review-export" onClick={() => setDrawerVisible(false)}>
                {t("menu.reviewExport")}
              </Link>
            ),
          },
        ]
      : []),
    ...(adminMenuItems.length > 0
      ? [{ key: "admin-divider", type: "divider" as const }, ...adminMenuItems]
      : []),
  ];

  const userMenuItems = [
    {
      key: "profile",
      label: <Link to="/profile">{t("menu.profile")}</Link>,
    },
    {
      key: "logout",
      label: (
        <>
          <LogoutOutlined /> {t("menu.logout")}
        </>
      ),
      onClick: handleLogout,
    },
  ];

  const renderUserSection = (compact = false) => (
    <>
      <LanguageSwitcher
        placement="header"
        size="small"
        showFlags={!compact}
        style={compact ? { minWidth: 84, width: 84 } : undefined}
      />

      {isAuthenticated ? (
        <>
          <ReminderCenter enabled={isAuthenticated} />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <span className="header-user-trigger">
              <Avatar className="header-user-avatar" icon={<UserOutlined />} />
              <span className="header-user-name">{user.username}</span>
            </span>
          </Dropdown>
        </>
      ) : (
        <Link to="/login" style={{ color: "#fff" }}>
          <UserOutlined style={{ marginRight: 8 }} />
          {t("menu.login")}
        </Link>
      )}
    </>
  );

  return (
    <Header className="responsive-header">
      <div className="header-content">
        <div className="logo">
          <Link to="/" className="brand-link">
            <img
              className="brand-logo"
              src="/android-chrome-192x192.png?v=20260611"
              alt=""
              aria-hidden="true"
            />
            <h2 style={{ color: "#fff", margin: 0 }}>{t("brand.name")}</h2>
          </Link>
        </div>

        {!isMobile && (
          <>
            <Menu
              theme="dark"
              mode="horizontal"
              selectedKeys={[selectedKey]}
              openKeys={desktopOpenKeys}
              triggerSubMenuAction="click"
              onOpenChange={(keys) => {
                const nextKeys = keys as string[];
                setDesktopOpenKeys(nextKeys.slice(-1));
              }}
              onClick={() => setDesktopOpenKeys([])}
              items={menuItems}
              className="desktop-menu"
              style={{ flex: 1, minWidth: 0 }}
            />
            <div className="user-section">{renderUserSection()}</div>
          </>
        )}

        {isMobile && (
          <div className="mobile-controls">
            <div className="mobile-user-section">{renderUserSection(true)}</div>
            <Button
              type="text"
              icon={<MenuOutlined />}
              aria-label={t("menu.openMenu", {
                defaultValue: "打开菜单",
              })}
              onClick={() => setDrawerVisible(true)}
              style={{ color: "#fff" }}
              className="mobile-menu-button"
            />
          </div>
        )}
      </div>

      <Drawer
        title={t("brand.name")}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        className="mobile-menu-drawer"
      >
        <Menu
          mode="vertical"
          selectedKeys={[selectedKey]}
          openKeys={mobileOpenKeys}
          onOpenChange={(keys) => setMobileOpenKeys(keys as string[])}
          items={menuItems}
          style={{ border: "none" }}
        />
      </Drawer>
    </Header>
  );
};

export default ResponsiveHeader;
